/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...

/*-----------------------------------------------------------------------------/
/ Pre-order info popovers: cart (snippets/cart-item.liquid, compact_notices)
/ and product page notices (snippets/pdp-notice.liquid)
/-----------------------------------------------------------------------------/
/ Closes the popover via its X button, or on any click outside it. Shared by
/ the cart drawer, the full cart page and the product page, so it lives here
/ rather than being loaded per render context.
/-----------------------------------------------------------------------------*/
document.addEventListener("click", (event) => {
  const closeButton = event.target.closest(".cart-item__preorder-info-close, .pdp-notice__info-close");
  if (closeButton) {
    closeButton.closest("details")?.removeAttribute("open");
    return;
  }

  document.querySelectorAll(".cart-item__preorder-info[open], .pdp-notice__info[open]").forEach((details) => {
    if (!details.contains(event.target)) {
      details.removeAttribute("open");
    }
  });
});

/*-----------------------------------------------------------------------------/
/ CartStore patch (assets/cart-store.js)
/-----------------------------------------------------------------------------/
/ /cart.js can occasionally answer with an HTML page instead of JSON
/ (throttling, expired preview session, challenge page). Unpatched,
/ CartStore.getCart() feeds that straight into .json(), which throws
/ "SyntaxError: Unexpected token <" and is rendered verbatim next to a
/ cart line item by cart-components.js's #renderError(). Retry with
/ backoff, then fail with a message a shopper can read. Also guard
/ subscribe(): cart-store notifies subscribers synchronously during
/ add/change/update, so one throwing subscriber otherwise aborts the
/ whole cart action for every other subscriber.
/
/ This module is loaded (type="module", right after cart-store.js and
/ before cart-link.js/cart-components.js) so the patch is applied
/ synchronously before any consumer calls CartStore.getCart() or
/ Cart.subscribe() — see layout/theme.liquid.
/-----------------------------------------------------------------------------*/
import { CartStore } from "cart-store";

const originalGetCart = CartStore.getCart;
const GETCART_RETRY_DELAYS_MS = [ 400, 800 ];

CartStore.getCart = async function patchedGetCart() {
  let lastError;
  for (let attempt = 0; attempt <= GETCART_RETRY_DELAYS_MS.length; attempt++) {
    try {
      return await originalGetCart();
    } catch (e) {
      lastError = e;
      if (attempt < GETCART_RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, GETCART_RETRY_DELAYS_MS[attempt]));
      }
    }
  }
  console.error("[cart-store patch] getCart failed after retries:", lastError);
  throw new Error("We couldn't load your cart. Please refresh the page and try again.");
};

const originalSubscribe = CartStore.prototype.subscribe;

CartStore.prototype.subscribe = function patchedSubscribe(callback) {
  const safeCallback = function (...args) {
    try {
      return callback.apply(this, args);
    } catch (e) {
      console.error("[cart-store patch] cart subscriber threw, ignoring:", e);
    }
  };
  return originalSubscribe.call(this, safeCallback);
};

/*-----------------------------------------------------------------------------/
/ Collection/search filter sidebar toggle (sections/main-collection-product-grid.liquid,
/ sections/main-search.liquid, "Sidebar" filter style, [data-toggle-filters] button)
/-----------------------------------------------------------------------------/
/ Desktop-only show/hide for the static filter sidebar (the theme's own filters-dialog
/ handles the mobile drawer separately). A delegated document listener, not a listener
/ bound to the button itself: dynamic-product-search.js swaps specific .js-* regions
/ (results, sort form, filter list, ...) on every filter/sort change and rebinds
/ listeners on *those*, but this button and the <dynamic-product-search> element it
/ toggles a class on both live outside all of them, so there's nothing to rebind here
/ after an update -- delegation just keeps working.
/-----------------------------------------------------------------------------*/
document.addEventListener("click", (event) => {
  const toggle = event.target.closest("[data-toggle-filters]");
  if (!toggle) return;
  const root = toggle.closest("dynamic-product-search");
  if (!root) return;
  const hidden = root.classList.toggle("is-filters-hidden");
  toggle.setAttribute("aria-expanded", String(!hidden));
});

/*-----------------------------------------------------------------------------/
/ Pill tabs sliding indicator (sections/section-collection.liquid, "Pill tabs &
/ cards" style, assets/component-tabs-collection-pill.css)
/-----------------------------------------------------------------------------/
/ tabbed-content.js (vendor) just flips aria-selected on click -- there's no
/ animation between tabs, only an instant background-color swap per button.
/ This adds a single absolutely-positioned bar per tab-triggers group that
/ slides/resizes to the clicked button, so switching tabs reads as one
/ continuous motion instead of a jump cut. Positioned/sized here rather than
/ in CSS because tab label widths vary per block/collection title.
/-----------------------------------------------------------------------------*/
function initPillTabIndicators() {
  document.querySelectorAll(".style--pill tab-triggers").forEach((tabTriggers) => {
    if (tabTriggers.querySelector(".tabs-pill-indicator")) return;

    const indicator = document.createElement("span");
    indicator.className = "tabs-pill-indicator";
    indicator.setAttribute("aria-hidden", "true");
    tabTriggers.prepend(indicator);

    const moveIndicatorTo = (button) => {
      if (!button) return;
      indicator.style.width = `${button.offsetWidth}px`;
      indicator.style.transform = `translateX(${button.offsetLeft}px)`;
    };

    const syncToSelected = () => moveIndicatorTo(tabTriggers.querySelector('[aria-selected="true"]'));

    tabTriggers.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", () => moveIndicatorTo(button));
    });

    new ResizeObserver(syncToSelected).observe(tabTriggers);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncToSelected);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPillTabIndicators);
} else {
  initPillTabIndicators();
}
document.addEventListener("shopify:section:load", initPillTabIndicators);
